/**
 * ViewWorkOrderWrapper
 *
 * Purpose:
 * - Wraps WO view page to route back into vendor orders using returnTab.
 */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { CONTRACTORS, WORK_ORDER, VENDOR, VENDOR_ORDERS } from "../../constants/route-paths";
import { getPurchaseOrder } from "../../actions/project-management.action";
import idx from "idx";
import { numberFormat } from "../../util/currency-formatter";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Table from 'react-bootstrap/Table';
import moment from 'moment';
import { Button } from "react-bootstrap";
import { VENDOR_TAB, VENDOR_URL_PARAM } from "../../constants/vendor.contant";

import WorkOrderInvoicesSection from "../../components/work-order-invoices-section";

function ViewWorkOrderWrapper({ getPurchaseOrder }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();

  const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB) || VENDOR_TAB.WORK_ORDERS;
  const returnSubTab = searchParams.get(VENDOR_URL_PARAM.RETURN_SUB_TAB);
  const [PO, setPO] = useState({});
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getPO();
  }, []);

  const getPO = async () => {
    const poId = params.poId;
    const query = `?poId=${poId}`;
    let res = await getPurchaseOrder(query);
    setPO(res);
    let totalCost = 0;

    Array.isArray(res.items) &&
      res.items.forEach(({ total }) => (totalCost += total));
    setTotal(totalCost);
  };

  const cancelHandler = () => {
    if (returnTab) {
      // Return to vendor orders with the correct tab and sub-tab
      let url = `/${VENDOR}/${VENDOR_ORDERS}?${VENDOR_URL_PARAM.TAB}=${returnTab}`;
      if (returnSubTab) {
        url += `&${VENDOR_URL_PARAM.SUB_TAB}=${returnSubTab}`;
      }
      navigate(url);
    } else {
      // Default to original contractor path
      navigate(`/${CONTRACTORS}/${WORK_ORDER}`);
    }
  };

  return (
    <div className="purchase-order-block" data-cy="wo-details">
      <h2 className="content-heading text-uppercase" data-cy="heading">View WO</h2>
      <div className="col-12 breadcrumb-block p-0" data-cy="breadcrumb">
        <Breadcrumb>
          <Breadcrumb.Item onClick={cancelHandler} data-cy="breadcrumb-orders">
            {returnTab ? "Orders" : "Work Order"}
          </Breadcrumb.Item>
          <Breadcrumb.Item active data-cy="breadcrumb-view-wo">View WO</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="row">
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="wo-number">
            <label>WO Number</label>
            {/* f_id is the WO number with project prefix and Suffix if null then default to wo Number*/}
            <p>{PO.f_id || PO.id}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="vendor-type">
            <label>Vendor Type</label>
            <p>Contractor</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="vendor-name">
            <label>Vendor Name</label>
            <p>{idx(PO, (_) => _.vendor.business_name) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="notes">
            <label>Notes</label>
            <p>{idx(PO, (_) => _.notes) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="street-address">
            <label>Street Address</label>
            <p>{idx(PO, (_) => _.street_address) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="suburb">
            <label>Suburb</label>
            <p>{idx(PO, (_) => _.suburb) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="state">
            <label>State</label>
            <p>{idx(PO, (_) => _.state) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="postcode">
            <label>Postcode</label>
            <p>{idx(PO, (_) => _.postcode) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="country">
            <label>Country</label>
            <p>{idx(PO, (_) => _.country) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="total">
            <label>Total</label>
            <p>{numberFormat(total)}</p>
          </div>
        </div>
      </div>

      <div className="material-list-block mt-3" data-cy="material-list">
        <div className="table-responsive">
          <Table bordered className="table-create table-material table-material-ch">
            <thead>
              <tr>
                <th className="w-auto" data-cy="name">Name</th>
                <th className="w-auto" data-cy="type">Type</th>
                <th className="text-right" data-cy="quotation">Quotation</th>
              </tr>
            </thead>
            <tbody data-cy="material-list-body">
              {(PO.items || []).map((item, index) => {
                return (
                  <tr key={item.id} data-cy="material-list-item">
                    <td data-cy="name">{item.taskName}</td>
                    <td data-cy="type">{item.taskDescription}</td>
                    <td className="text-right" data-cy="quotation">{numberFormat(item.total)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </div>

      <WorkOrderInvoicesSection
        poUuid={params.poId}
        projectId={PO.projectId}
        vendorType="sub-contractor"
        onNavigate={(invoiceId) => navigate(`/vendor/invoices/contractor/view/${invoiceId}?id=${PO.projectId}&poId=${params.poId}&type=contractor`)}
      />

      <div className="col-12 d-flex mt-3 justify-content-end">
        <Button type="submit" onClick={cancelHandler} className="secondarybtn cancelbtn" data-cy="cancel-button">BACK</Button>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  getPurchaseOrder: bindActionCreators(getPurchaseOrder, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewWorkOrderWrapper);
